import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LinkSimple, Copy, Check, CurrencyDollar, ShareNetwork, Tag, Plus, Trash } from "@phosphor-icons/react";

export default function InstructorReferralCard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const isInstructor = user && ["level1_instructor", "level2_instructor"].includes(user.role);

  useEffect(() => {
    if (!isInstructor) return;
    (async () => {
      try { const { data } = await api.get("/instructor/referral-stats"); setStats(data); }
      catch (e) { setErr(formatApiError(e)); }
    })();
  }, [isInstructor]);

  if (!isInstructor) return null;
  if (!stats && !err) return null;

  const code = stats?.referral_code || "";
  const refLink = `${window.location.origin}/catalog?ref=${code}`;
  const copyText = async (text, onOk) => {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); ok = true; }
    } catch (_) {}
    if (!ok) {
      try {
        const ta = document.createElement("textarea"); ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); ok = true;
      } catch (_) {}
    }
    if (ok) onOk(); else setErr("Copy not supported here. Long-press to copy manually.");
  };
  const copy = () => copyText(refLink, () => { setCopied(true); setTimeout(() => setCopied(false), 1800); });
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "The Self-Agency Project", text: `Level up with The Self-Agency Project. Use my code ${code}:`, url: refLink }); return; }
      catch (_) {}
    }
    copy();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card flex flex-col gap-4" data-testid="instructor-referral-card">
      <div className="flex items-center gap-2">
        <ShareNetwork size={26} weight="duotone" color="#00E5FF" />
        <h2 className="text-xl font-bold font-display">Your referral code</h2>
      </div>
      <p className="text-sm text-[#000000] -mt-2">
        Tell students <span className="text-[#000000] font-semibold">"use code {code || "…"}"</span> — or share your link. When they buy, you earn{" "}
        <span className="text-[#7a8a00] font-bold">{stats?.payout_percentage || 0}%</span> automatically.
      </p>

      {/* Prominent code */}
      <div className="flex items-center gap-3 rounded-2xl border border-[#C4C8CE] bg-[#F7FEE7] p-3">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#000000]">Your code</div>
          <div className="font-mono font-extrabold text-2xl tracking-[0.15em] text-[#000000]" data-testid="referral-code-text">{code || "—"}</div>
        </div>
        <button data-testid="copy-code-button" onClick={() => copyText(code, () => { setCopied(true); setTimeout(() => setCopied(false), 1800); })} className="btn-secondary text-xs">Copy code</button>
      </div>

      {err && <div className="text-[#FF5100] text-sm">{err}</div>}

      <div className="flex items-stretch gap-2">
        <div className="flex-1 min-w-0 px-3 py-2.5 rounded-xl bg-[#DCDEE2] border border-[#C4C8CE] font-mono text-xs truncate flex items-center" data-testid="referral-link-text">
          {refLink}
        </div>
        <button data-testid="copy-referral-button" onClick={copy} className="btn-primary px-4 flex items-center gap-1 shrink-0">
          {copied ? <Check size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
          {copied ? "Copied" : "Copy"}
        </button>
        <button data-testid="share-referral-button" onClick={share} className="btn-secondary px-3 shrink-0" title="Share">
          <LinkSimple size={16} weight="bold" />
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-2 mt-1">
          <MiniStat label="Referrals" value={stats.referred_purchases_count} accent="#00E5FF" testid="ref-stat-count" />
          <MiniStat label="Revenue" value={`$${(stats.total_referred_revenue_cents/100).toFixed(2)}`} accent="#FFD600" testid="ref-stat-revenue" />
          <MiniStat label="Your share" value={`$${(stats.total_payout_earned_cents/100).toFixed(2)}`} accent="#CCFF00" testid="ref-stat-payout" />
        </div>
      )}

      <PromoCodeManager />

      {stats && stats.recent_referrals.length > 0 && (
        <div className="mt-1">
          <div className="text-xs uppercase tracking-[0.2em] text-[#000000] mb-2 flex items-center gap-1.5">
            <CurrencyDollar size={14} /> Recent referrals
          </div>
          <ol className="flex flex-col gap-1">
            {stats.recent_referrals.slice(0, 5).map((r, i) => (
              <li key={i} data-testid={`ref-recent-${i}`} className="flex items-center justify-between text-sm py-1.5 border-b border-[#C4C8CE] last:border-0">
                <span className="truncate text-[#000000]">{r.course_title}{r.promo_code ? ` · ${r.promo_code}` : ""}</span>
                <span className="text-[#7a8a00] font-bold ml-2 shrink-0">+${(r.payout_cents/100).toFixed(2)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {stats && stats.recent_referrals.length === 0 && (
        <div className="text-xs text-[#000000] italic">No referrals yet — share your code to start earning.</div>
      )}
    </motion.div>
  );
}

function PromoCodeManager() {
  const [codes, setCodes] = useState([]);
  const [cap, setCap] = useState(25);
  const [code, setCode] = useState("");
  const [pct, setPct] = useState(10);
  const [maxUses, setMaxUses] = useState("");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/instructor/promo-codes"); setCodes(data.promo_codes || []); setCap(data.max_discount_percent || 25); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const body = { code: code.trim(), discount_percent: Number(pct) };
      if (maxUses) body.max_uses = Number(maxUses);
      if (expires) body.expires_on = expires;
      await api.post("/instructor/promo-codes", body);
      setCode(""); setPct(10); setMaxUses(""); setExpires(""); await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/instructor/promo-codes/${id}`); await load(); } catch (e) { setErr(formatApiError(e)); }
  };

  const status = (p) => {
    const today = new Date().toISOString().slice(0, 10);
    if (p.expires_on && p.expires_on < today) return { label: "expired", cls: "bg-[#FEE2E2] text-[#B91C1C]" };
    if (p.max_uses != null && (p.uses_count || 0) >= p.max_uses) return { label: "used up", cls: "bg-[#FEE2E2] text-[#B91C1C]" };
    return null;
  };

  return (
    <div className="rounded-2xl border border-[#C4C8CE] bg-white p-4 flex flex-col gap-3" data-testid="promo-code-manager">
      <div className="flex items-center gap-2">
        <Tag size={18} weight="fill" color="#84CC16" />
        <h3 className="font-bold font-display">Discount codes</h3>
        <span className="text-xs text-[#000000]">give students up to {cap}% off — you still earn on the discounted price</span>
      </div>

      <form onSubmit={create} noValidate className="flex flex-wrap items-end gap-2" data-testid="promo-create-form">
        <div className="flex-1 min-w-[130px]">
          <label className="text-[10px] uppercase tracking-wider text-[#000000]">Code name</label>
          <input data-testid="promo-code-input" className="input-base w-full mt-1" placeholder="e.g. SPRING10" value={code} onChange={(e) => setCode(e.target.value)} maxLength={24} />
        </div>
        <div className="w-20">
          <label className="text-[10px] uppercase tracking-wider text-[#000000]">% off</label>
          <input data-testid="promo-pct-input" type="number" min={1} max={cap} className="input-base w-full mt-1" value={pct} onChange={(e) => setPct(e.target.value)} />
        </div>
        <div className="w-24">
          <label className="text-[10px] uppercase tracking-wider text-[#000000]">Max uses</label>
          <input data-testid="promo-maxuses-input" type="number" min={1} className="input-base w-full mt-1" placeholder="∞" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
        </div>
        <div className="w-36">
          <label className="text-[10px] uppercase tracking-wider text-[#000000]">Expires</label>
          <input data-testid="promo-expires-input" type="date" className="input-base w-full mt-1" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </div>
        <button data-testid="promo-create-button" type="submit" disabled={busy || !code.trim()} className="btn-primary flex items-center gap-1"><Plus size={15} weight="bold" /> Create</button>
      </form>
      {err && <div className="text-[#FF5100] text-sm" data-testid="promo-error">{err}</div>}

      {codes.length === 0 ? (
        <div className="text-xs text-[#000000] italic">No discount codes yet. Create one above (e.g. "SPRING10" for 10% off, first 50 uses, expires Apr 30).</div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {codes.map((p) => {
            const st = status(p);
            return (
              <div key={p.id} data-testid={`promo-row-${p.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-[#E4E4E7] px-3 py-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className="font-mono font-bold text-[#000000]">{p.code}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534]">{p.discount_percent}% off</span>
                  <span data-testid={`promo-uses-${p.id}`} className="text-[11px] text-[#000000]">{p.uses_count || 0}{p.max_uses != null ? `/${p.max_uses}` : ""} used</span>
                  {p.expires_on && <span className="text-[11px] text-[#000000]">· expires {p.expires_on}</span>}
                  {st && <span data-testid={`promo-status-${p.id}`} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${st.cls}`}>{st.label}</span>}
                </div>
                <button data-testid={`promo-delete-${p.id}`} onClick={() => remove(p.id)} className="btn-ghost text-[#FF5100] shrink-0" title="Delete code"><Trash size={15} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent, testid }) {
  return (
    <div className="rounded-xl bg-[#DCDEE2] border border-[#C4C8CE] px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#000000]">{label}</div>
      <div className="text-lg font-extrabold font-display" style={{ color: accent }}>{value}</div>
    </div>
  );
}
